// Inspecteur → Wet- en regelgeving (per-tenant legal-source activation) — FRONTEND-BLUEPRINT §3.1
// + PLAN-W27 B. The canonical Regulation catalog (tenancy.ts regulation + jurisdiction) with this
// tenant's enabled state (tenant_regulation_enablement): a table of name · jurisdiction · status,
// with inline Toevoegen (enable) / Verwijderen (disable) riding application_regulation.write.
// Canonical regulation CONTENT beyond the small factual seed is owner/counsel-gated, so the empty
// state explains that the catalog fills as the owner approves regulations.
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { api } from "../../lib/api";
import { Can } from "../../lib/rbac";
import { useI18n } from "../../lib/i18n";
import { PageHeader } from "@/components/page-header";
import { DataTable, type Column } from "@/components/data-table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { errText, TagBadge, type Row } from "./_shared";

export function InspectorRegulationsRoute() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { data, isLoading, error } = useQuery({ queryKey: ["regulations"], queryFn: () => api.listRegulations() });
  const rows = (data ?? []) as Row[];

  const toggle = useMutation({
    mutationFn: ({ regulationId, enabled }: { regulationId: string; enabled: boolean }) =>
      api.setRegulationEnabled(regulationId, enabled),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["regulations"] }),
  });

  const columns: Column<Row>[] = [
    {
      key: "title", header: t("regs.col.name"),
      render: (r) => (
        <span className="inline-flex items-center gap-2">
          <span className="font-medium">{String(r.title ?? "—")}</span>
          {r.shortCode ? <span className="font-mono text-xs text-muted-foreground">{String(r.shortCode)}</span> : null}
        </span>
      ),
    },
    { key: "jurisdiction", header: t("regs.col.jurisdiction"), render: (r) => <TagBadge value={r.jurisdictionName ?? r.jurisdictionCode} /> },
    {
      key: "enabled", header: t("regs.col.status"),
      render: (r) => (
        <Badge variant={r.enabled ? "default" : "outline"}>{r.enabled ? t("regs.enabled") : t("regs.disabled")}</Badge>
      ),
    },
  ];

  return (
    <section>
      <PageHeader title={t("nav.regulations")} description={t("regs.sub")} />
      {error && <p className="mb-3 text-sm text-destructive">{t("crud.failed")} {errText(error)}</p>}
      {toggle.isError && <p className="mb-3 text-sm text-destructive">{t("regs.saveFailed")} {errText(toggle.error)}</p>}
      <DataTable<Row>
        columns={columns}
        rows={rows}
        loading={isLoading}
        empty={t("regs.empty")}
        getRowId={(r) => String(r.regulationId ?? "")}
        onRowClick={(r) => { if (r.regulationId) void navigate({ to: "/inspector/regulations/$id", params: { id: String(r.regulationId) } }); }}
        rowActions={(r) => {
          const enabled = Boolean(r.enabled);
          return (
            <Can perm="application_regulation.write">
              <Button
                variant="ghost"
                size="xs"
                className={enabled ? "text-destructive" : "text-primary"}
                disabled={toggle.isPending}
                onClick={() => toggle.mutate({ regulationId: String(r.regulationId), enabled: !enabled })}
              >
                {enabled ? t("regs.remove") : t("regs.add")}
              </Button>
            </Can>
          );
        }}
      />
    </section>
  );
}
